// models
import { ResMapRestriction } from 'containers/Dashboard/Map/Gs/GoogleMapTest';
import type {
  ReqAddProjectImage,
  ReqDeleteProjectImage,
  ReqProjectDownload,
  ReqProjectInfo,
  ReqProjectModify,
  ResProjectInfo,
  ResProjectModify,
  ProjectDetail,
  ProjectDetailInfo,
  WorkAreaSettingDto,
  ReqProjectList,
  ReqCreateProject,
  ReqUpdateProject,
  ReqAddProjectPhoto,
  ReqDeleteProjectPhoto
} from '../models/project.model';
import type { BaseApiResponse, ResultBaseProps } from 'modules/common';

// services
import AxiosInstanceCreator from 'services/api';

// libraries
import { type AxiosInstance } from 'axios';

const projectInstance = new AxiosInstanceCreator().create();

export const projectApi = {
  /**
   * 프로젝트 상세 조회
   *
   * @param {ReqProjectInfo} data
   * @returns {ResProjectInfo}
   */
  async getProjectInfo(data: ReqProjectInfo): Promise<ResProjectInfo> {
    return await projectInstance
      .get<ResProjectInfo>(`/project/${data.projectId}`)
      .then((res) => res.data);
  },

  /**
   * 프로젝트 조감도 조회
   *
   * @param {ReqProjectDownload} data
   * @returns {Blob}
   */
  async getProjectDownload(data: ReqProjectDownload): Promise<string> {
    return await projectInstance
      .get<Blob>(`/project/downloads/${data.fileId}`, {
        responseType: 'blob'
      })
      .then((res) => {
        let imageURL = URL.createObjectURL(res.data);

        return imageURL;
      });
  },

  /**
   * 프로젝트 업데이트
   *
   * @param {ReqProjectModify} data
   * @returns {ResProjectModify}
   */
  async updateProject(data: ReqProjectModify): Promise<ResProjectModify> {
    return await projectInstance
      .post<ResProjectModify>('/project/modify', data)
      .then((res) => res.data);
  },

  /**
   * 프로젝트 이미지 파일 추가
   *
   * @param {ReqAddProjectImage} data
   * @returns {ResultBaseProps}
   */
  async addProjectImage(data: ReqAddProjectImage): Promise<ResultBaseProps> {
    return await projectInstance
      .post<ResultBaseProps>(`/project/${data.id}/photo`, data.files, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((res) => res.data);
  },

  /**
   * 프로젝트 이미지 파일 삭제
   *
   * @param {ReqDeleteProjectImage} data
   * @returns {ResultBaseProps}
   */
  async deleteProjectImage(data: ReqDeleteProjectImage): Promise<ResultBaseProps> {
    return await projectInstance
      .delete<ResultBaseProps>(`/project/${data.id}/photo`, { params: { fileId: data.fileId } })
      .then((res) => res.data);
  },

  /**
   * 프로젝트 작업 지역 업데이트
   *
   * @param {string} projectId
   * @param {ResMapRestriction} data
   * @returns {ResultBaseProps}
   */
  async updateWorkArea(projectId: string, data: ResMapRestriction): Promise<ResultBaseProps> {
    return await projectInstance
      .post<ResultBaseProps>(`/project/${projectId}/workArea`, data)
      .then((res) => res.data);
  }
};

export class ProjectAPI {
  Axios: AxiosInstance;

  constructor() {
    this.Axios = new AxiosInstanceCreator({
      baseURL: '/v1/api/project'
    }).create();
  }

  /** 프로젝트 목록 조회 API */
  async getProjectList(
    params: ReqProjectList
  ): Promise<BaseApiResponse<unknown, ProjectDetailInfo>> {
    return await this.Axios.get<BaseApiResponse<unknown, ProjectDetailInfo>>('', { params }).then(
      (res) => res.data
    );
  }

  /** 프로젝트 생성 API */
  async saveProject(params: ReqCreateProject): Promise<BaseApiResponse<ProjectDetailInfo>> {
    const formData = new FormData();
    const { files, ...projectData } = params;

    if (files?.length) {
      files.forEach((file) => formData.append('files', file));
    }

    Object.entries(projectData).forEach(([key, value]) => {
      if (value !== undefined) {
        if (typeof value === 'object' && value !== null) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    return await this.Axios.post<BaseApiResponse<ProjectDetailInfo>>('', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then((res) => res.data);
  }

  /** 프로젝트 상세 조회 API */
  async getProjectDetail(projectId: string): Promise<BaseApiResponse<ProjectDetail>> {
    return await this.Axios.get<BaseApiResponse<ProjectDetail>>(`/${projectId}`).then(
      (res) => res.data
    );
  }

  /** 프로젝트 삭제 API */
  async deleteProject(projectId: string): Promise<BaseApiResponse<string>> {
    return await this.Axios.delete<BaseApiResponse<string>>(`/${projectId}`).then(
      (res) => res.data
    );
  }

  /** 프로젝트 수정 API */
  async updateProject(params: ReqUpdateProject): Promise<BaseApiResponse<ProjectDetailInfo>> {
    return await this.Axios.post<BaseApiResponse<ProjectDetailInfo>>('/modify', params).then(
      (res) => res.data
    );
  }

  /** 프로젝트 작업위치 설정 API */
  async setWorkArea(
    projectId: string,
    params: WorkAreaSettingDto
  ): Promise<BaseApiResponse<WorkAreaSettingDto>> {
    return await this.Axios.post<BaseApiResponse<WorkAreaSettingDto>>(
      `/${projectId}/workArea`,
      params
    ).then((res) => res.data);
  }

  /** 프로젝트 이미지 추가 API */
  async addProjectPhoto(params: ReqAddProjectPhoto): Promise<BaseApiResponse<boolean>> {
    const formData = new FormData();

    const { projectId, files } = params;

    if (files) {
      files.forEach((file) => formData.append('files', file));
    }

    return await this.Axios.post<BaseApiResponse<boolean>>(`/${projectId}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then((res) => res.data);
  }

  /** 프로젝트 이미지 삭제 API */
  async deleteProjectPhoto(params: ReqDeleteProjectPhoto): Promise<BaseApiResponse<boolean>> {
    return await this.Axios.delete<BaseApiResponse<boolean>>(`/${params.projectId}/photo`, {
      params: { fileId: params.fileId }
    }).then((res) => res.data);
  }

  /** 프로젝트 파일 다운로드 API */
  async downloadProjectFile(fileId: string): Promise<Blob> {
    return await this.Axios.get<Blob>(`/downloads/${fileId}`, {
      responseType: 'blob'
    }).then((res) => res.data);
  }
}
